import { zodResolver } from "@hookform/resolvers/zod";
import { ActionIcon, Badge, Button, Card, Divider, Stack, Text, Title, Tooltip } from "@mantine/core";
import { modals } from "@mantine/modals";
import { PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { Path, useFieldArray, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import z from "zod";

import { Fields } from "@/components/_Fields";
import { Input } from "@/components/_Input";
import { TransferCallbackParams, TransferListColumn, TransferParams } from "@/components/_Input/TransferList";
import { DashboardComponents } from "@/features/dashboard/_components/dashboard";
import { driverApi, useBindShippingCompanyMutation, useLazyGetDriversGroupQuery, useLazyGetTruckQuery, useUnbindDriverShippingCompanyMutation } from "@/store";
import { useAppDispatch } from "@/store/hooks";

export const Route = createFileRoute("/_private/dashboard/_base/conjuntos/vincular/transportadora")({
  component: RouteComponent
});

type Schema = z.infer<typeof schema>;
type ListItem = Schema["groups"]["left"][number];

// Schema for the list items (groups) - stores complete Truck objects
const groupItemSchema = z.object({
  truck: z.any(), // Store complete Truck object from API
  driver: z.any() // Store complete Driver object from API
});

const schema = z.object({
  shippingCompany: z.object({
    id: z.string()
  }),
  truck: z.object({
    id: z.string({ error: "Teste" }),
    licensePlate: z.string(),
    vehicleModel: z.object({
      id: z.string(),
      name: z.string(),
      truckBrand: z.object({
        id: z.string(),
        name: z.string()
      })
    }),
    isEnabled: z.boolean().optional()
  }),
  driver: z.object({
    id: z.string(),
    name: z.string(),
    isEnabled: z.boolean().optional(),
    company: z.object({
      id: z.string()
    })
  }),
  groups: z.object({
    left: z.array(groupItemSchema),
    right: z.array(groupItemSchema)
  })
});

function RouteComponent() {
  const dispatch = useAppDispatch();
  const [driversTrigger, { isFetching }] = useLazyGetDriversGroupQuery();
  const [truckTrigger, { data: truckData, isFetching: isFetchingTruck, isSuccess }] = useLazyGetTruckQuery();
  const [bindShippingCompany, { isLoading: isBinding }] = useBindShippingCompanyMutation();
  const [unbindShippingCompany, { isLoading: isUnbinding }] = useUnbindDriverShippingCompanyMutation();

  const {
    formState: { isSubmitting: _isSubmitting, isDirty: _isDirty },
    resetField,
    setValue,
    control,
    watch
  } = useForm({
    mode: "all",
    disabled: isFetching || isFetchingTruck || isBinding || isUnbinding,
    resolver: zodResolver(schema),
    defaultValues: {
      shippingCompany: {
        id: ""
      },
      truck: {
        id: "",
        licensePlate: "",
        vehicleModel: {
          id: "",
          name: "",
          truckBrand: {
            id: "",
            name: ""
          }
        }
      },
      driver: {
        id: "",
        name: "",
        isEnabled: false,
        company: {
          id: ""
        }
      },
      groups: {
        left: [],
        right: []
      }
    }
  });
  const { fields, append, remove } = useFieldArray({
    keyName: "_id",
    control,
    name: "groups.left"
  });

  const [shippingCompanyId, truckId] = watch(["shippingCompany.id", "truck.id"]);

  const handleTransfer = (params: TransferCallbackParams<ListItem> | TransferParams<ListItem>) => {
    // A lógica de transferência agora é tratada no handleBeforeTransfer
    // Este callback é chamado apenas após a transferência bem-sucedida
    console.log("Transferência concluída:", params);
  };

  const handleBeforeTransfer = async (params: TransferParams<ListItem>) => {
    const { from, to, items } = params;

    const getTransferText = () => {
      const direction = `${from}_${to}` as const;
      const isSingle = items.length === 1;

      const textMap = new Map([
        ["left_right_single", `Você tem certeza que deseja vincular o motorista ${items[0].driver.name}?`],
        ["left_right_multiple", `Você tem certeza que deseja vincular ${items.length} conjunto(s)?`],
        ["right_left_single", `Você tem certeza que deseja desvincular o motorista ${items[0].driver.name}?`],
        ["right_left_multiple", `Você tem certeza que deseja desvincular ${items.length} conjunto(s)?`],
        ["default_single", `Você tem certeza que deseja transferir o motorista ${items[0].driver.name}?`],
        ["default_multiple", `Você tem certeza que deseja transferir ${items.length} item(s)?`]
      ]);

      const key = textMap.has(`${direction}_${isSingle ? "single" : "multiple"}`)
        ? `${direction}_${isSingle ? "single" : "multiple"}`
        : `default_${isSingle ? "single" : "multiple"}`;

      return textMap.get(key);
    };

    // Primeiro, pede confirmação do usuário
    const confirmed = await new Promise<boolean>((resolve) => {
      modals.openConfirmModal({
        title: "Confirmação de Transferência",
        children: (
          <Stack>
            <Text size="sm">{getTransferText()}</Text>
          </Stack>
        ),
        labels: { confirm: "Confirmar", cancel: "Cancelar" },
        centered: true,
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false)
      });
    });

    // Se o usuário cancelou, retorna false
    if (!confirmed) return false;

    // Se confirmou, executa a operação apropriada usando Map
    const matcher = new Map<"left_right" | "right_left", () => Promise<boolean>>([
      [
        "left_right",
        async () => {
          // Vincular - executar bindShippingCompany
          const companyId = items[0]?.driver?.company?.id;

          if (!companyId) {
            toast.error("Erro ao obter ID da empresa");
            return false;
          }

          const body = {
            shippingCompany: {
              id: Number(watch("shippingCompany.id"))
            },
            company: {
              id: Number(companyId)
            },
            groups: items
          };

          await bindShippingCompany(body).unwrap();
          toast.success("Conjunto(s) vinculado(s) com sucesso!");
          return true;
        }
      ],
      [
        "right_left",
        async () => {
          // Desvincular - executar unbindShippingCompany
          const ids = items.map((item) => item.driver.id) as number[];

          if (ids.length === 0) {
            toast.error("Selecione pelo menos um conjunto para desvincular");
            return false;
          }

          // Executar todas as operações de desvinculação
          const promises = ids.map((id) => unbindShippingCompany({ id }).unwrap());
          await Promise.all(promises);

          toast.success("Conjunto(s) desvinculado(s) com sucesso!");
          return true;
        }
      ]
    ]);

    try {
      const direction = `${from}_${to}` as "left_right" | "right_left";
      const handler = matcher.get(direction);

      if (!handler) {
        console.error(`Invalid direction ${direction}`);
        return false;
      }

      return await handler();
    } catch (error) {
      console.error("Erro na operação:", error);
      toast.error("Erro ao processar a operação. Tente novamente.");
      return false;
    }
  };

  type ClearKey = Path<Schema>;
  const handleClear = (key: ClearKey) => () => {
    setValue(key, "");

    const resetApi = () => dispatch(driverApi.util.resetApiState());
    const matcher = new Map<ClearKey, () => void>([
      [
        "shippingCompany.id",
        () => {
          setValue("groups.left", []);
          setValue("groups.right", []);
          resetApi();
        }
      ],
      [
        "truck.id",
        () => {
          resetApi();
        }
      ]
    ]);
    const keys = Array.from(matcher.keys());
    const handler = matcher.get(key);
    if (!handler) return console.error(`Invalid key ${key}, are accepted only ${keys.join(", ")}!`);
    handler();
  };

  type BindParams = { type: "remove"; id: number } | { type: "append"; id?: number };
  const handleBind =
    ({ type, id }: BindParams) =>
    () => {
      const matcher = new Map<BindParams["type"], () => void>([
        [
          "append",
          () => {
            if (!isSuccess) return;

            const hasItem = fields.find((field) => field.driver.id === truckData.driver.id);

            if (hasItem) return toast.error("Motorista já adicionado!");

            console.log({ truckData });

            resetField("driver.id");
            resetField("truck.id");
            dispatch(driverApi.util.resetApiState());
            // Store complete truck and driver objects from API
            append({
              truck: truckData, // Complete Truck object
              driver: { ...truckData.driver, company: truckData.company } // Complete Driver object
            });
          }
        ],
        [
          "remove",
          () => {
            const index = fields.findIndex((field) => field.driver.id === id);
            remove(index);
          }
        ]
      ]);
      const keys = Array.from(matcher.keys());
      const handler = matcher.get(type);

      if (!handler) return console.error(`Invalid type ${type}, Only accepted ${keys.join(", ")!}`);
      handler();
    };

  type TriggerType = "truck" | "driverGroup";
  const handleTrigger = async (type: TriggerType | TriggerType[]) => {
    const pendingOps = new Set<TriggerType>();
    const matcher = new Map<TriggerType, () => Promise<void>>([
      [
        "truck",
        async () => {
          if (!truckId) return console.error("truckId is not defined");
          const data = await truckTrigger({ id: Number(truckId) }).unwrap();
          setValue("driver.id", String(data.id));
        }
      ],
      [
        "driverGroup",
        async () => {
          if (!shippingCompanyId) return console.error("shippingCompanyId is not defined");

          const { data } = await driversTrigger({ shippingCompanyId }).unwrap();
          const rows = data.map((driver) => {
            const truck = driver.truck[0];

            return {
              truck: truck, // Complete Truck object from API
              driver: driver // Complete Driver object from API
            };
          });

          setValue("groups.right", rows);
        }
      ]
    ]);

    const types = Array.isArray(type) ? type : [type];

    const promises = types
      .filter((t) => !pendingOps.has(t))
      .map(async (t) => {
        pendingOps.add(t);
        try {
          const handler = matcher.get(t);
          if (!handler) return console.error(`Invalid type ${t}, Only accepted ${Array.from(matcher.keys()).join(", ")!}`);
          await handler();
        } finally {
          pendingOps.delete(t);
        }
      });

    await Promise.all(promises);
  };

  useEffect(() => {
    handleTrigger(["driverGroup", "truck"]);
  }, [shippingCompanyId, truckId]);

  return (
    <Stack>
      <DashboardComponents.Title>Gerenciar Conjuntos / Transportadora</DashboardComponents.Title>

      <Stack>
        <Title order={3}>Transportadora</Title>

        <Card component={Stack} withBorder>
          <Fields.ShippingCompanies control={control} name="shippingCompany.id" className="self-start" onClear={handleClear("shippingCompany.id")} />

          <Stack gap={0}>
            <Title order={4}>Selecionar Conjuntos</Title>
            <Card component={Stack} withBorder>
              <div className="grid grid-cols-1 gap-[inherit] md:grid-cols-2">
                <Fields.Trucks
                  control={control}
                  name="truck.id"
                  disabled={!shippingCompanyId}
                  getLabel={(truck) => `${truck.id} - ${truck.licensePlate}`}
                  onClear={handleClear("truck.id")}
                />
                <Fields.Drivers
                  control={control}
                  name="driver.id"
                  disabled={!shippingCompanyId}
                  getLabel={(driver) => `${driver.id} - ${driver.name}`}
                  onClear={handleClear("driver.id")}
                  searchValue={truckData?.driver?.name}
                  readOnly
                />
              </div>

              {truckData && (
                <Card shadow="md" withBorder className="self-start">
                  <div className="flex flex-wrap gap-2">
                    <div className="flex flex-col">
                      <Text fw="bold" size="lg">
                        Motorista:
                      </Text>
                      <div className="flex items-center gap-1">
                        <Text size="sm">{truckData?.driver.id}</Text>

                        <Badge size="sm" color={truckData.driver.isEnabled ? "green" : "red"}>
                          {truckData.driver.isEnabled ? "Ativo" : "Inativo"}
                        </Badge>
                      </div>

                      <Text>{truckData?.driver.name}</Text>
                    </div>

                    <Divider orientation="vertical" />

                    <div className="flex flex-col">
                      <Text fw="bold" size="lg">
                        Caminhão:
                      </Text>
                      <div className="flex items-center gap-1">
                        <Text size="sm">{truckData?.id}</Text>

                        <Badge size="sm" color={truckData.isEnabled ? "green" : "red"}>
                          {truckData.isEnabled ? "Ativo" : "Inativo"}
                        </Badge>
                      </div>

                      <Text>
                        {truckData?.licensePlate} - {truckData.vehicleModel.truckBrand.name} - {truckData.vehicleModel.name}
                      </Text>
                    </div>
                  </div>
                </Card>
              )}

              <Button
                leftSection={<PlusIcon weight="bold" />}
                className="md:self-end"
                onClick={handleBind({ type: "append" })}
                disabled={!shippingCompanyId || !truckId || !isSuccess}
              >
                Incluir
              </Button>
            </Card>
          </Stack>
        </Card>
      </Stack>

      <Card component={Stack} withBorder>
        <Title order={3}>Conjuntos Disponíveis</Title>

        <Input.TransferList
          pagination={{
            pageSize: 5
          }}
          control={control}
          name="groups"
          variant="table"
          leftTitle="Lista para vincular"
          rightTitle="Vinculados"
          disabled={!shippingCompanyId}
          columns={
            [
              {
                key: "driver" as "id",
                label: "ID",
                align: "left",
                headerAlign: "left",
                width: 80,
                valueGetter: (item) => item.driver.id
              },
              {
                key: "driver" as "name",
                label: "Motorista",
                width: 250,
                valueGetter: (item) => item.driver.name.toUpperCase(),
                render: (_value, row) => {
                  const { id, isEnabled, name } = row.driver;

                  return (
                    <div className="flex flex-col">
                      <div className="flex gap-2">
                        <Text size="sm">{id}</Text>
                        <Badge size="sm" color={isEnabled ? "green" : "red"}>
                          {isEnabled ? "Ativo" : "Inativo"}
                        </Badge>
                      </div>

                      <Text size="sm" fw={500}>
                        {name.toUpperCase()}
                      </Text>
                    </div>
                  );
                }
              },
              {
                key: "truck",
                label: "Caminhão",
                width: 250,
                render: (_value, row) => {
                  const { id, isEnabled, licensePlate, vehicleModel } = row.truck;
                  const modelName = vehicleModel.name.toUpperCase();
                  const brandName = vehicleModel.truckBrand.name.toUpperCase();

                  return (
                    <div className="flex flex-col">
                      <div className="flex gap-2">
                        <Text size="sm">{id}</Text>
                        <Badge size="sm" color={isEnabled ? "green" : "red"}>
                          {isEnabled ? "Ativo" : "Inativo"}
                        </Badge>
                      </div>

                      {(licensePlate || brandName || modelName) && (
                        <Text size="sm" fw={500}>
                          {licensePlate} {brandName && `- ${brandName}`} {modelName && `- ${modelName}`}
                        </Text>
                      )}
                    </div>
                  );
                }
              },
              {
                key: "driver" as "actions",
                label: "Ações",
                align: "center",
                headerAlign: "center",
                hide: ({ side }) => side === "right",
                render: (_value, item) => (
                  <Tooltip label="Remover" color="red">
                    <ActionIcon variant="light" radius="xl" color="red" onClick={handleBind({ type: "remove", id: Number(item.driver.id) })}>
                      <TrashIcon />
                    </ActionIcon>
                  </Tooltip>
                )
              }
            ] as TransferListColumn<ListItem>[]
          }
          tooltips={{
            labels: {
              leftAll: "Vincular todos os conjuntos",
              leftSelected: "Vincular conjuntos selecionados",
              rightAll: "Desvincular todos os conjuntos",
              rightSelected: "Desvincular conjuntos selecionados"
            },
            props: {
              position: "top",
              withArrow: true,
              color: "blue"
            }
          }}
          onDuplicateDetected={() => toast.error("Não são permitidos itens repetidos!")}
          onTransfer={handleTransfer}
          onTransferAll={handleTransfer}
          onBeforeTransfer={handleBeforeTransfer}
          getItemLabel={(item) => item.driver.name}
          getItemValue={(item) => item.driver.id}
          loading={isFetching || isBinding || isUnbinding}
        />
      </Card>
    </Stack>
  );
}
